import type { ReactNode } from 'react';

import { useTranslation } from '../i18n/useTranslation';

import { cn } from './cn';
import { LumeMark } from './LumeMark';

// ============================================================
// Lume / TEACare PWA — SplashLoader
// ============================================================
// Loader de transição com identidade Lume. Substitui o Spinner
// genérico em fallbacks de Suspense para apresentações.
//
// Princípios respeitados:
//   - 1 (literal): texto "Carregando" em pt-BR.
//   - 4/11 (sem som / autoplay): puramente visual.
//   - 10 (prefers-reduced-motion): pulso desligado por CSS global
//     (`*::before { animation-duration: 0.01ms !important }` em
//     index.css). Nenhuma animação infinita > 2s.
//
// Tokens semânticos exclusivamente — zero `bg-white`, `text-neutral-*`.
// ============================================================

export interface SplashLoaderProps {
  /** Texto literal para leitores de tela. Default: "Carregando". */
  label?: string;
  /** Tela cheia (centraliza vertical+horizontal). Default true. */
  fullscreen?: boolean;
  className?: string;
  /** Subtítulo opcional para contexto. */
  subtitle?: ReactNode;
}

export function SplashLoader({
  label,
  fullscreen = true,
  className,
  subtitle,
}: SplashLoaderProps): ReactNode {
  const { t } = useTranslation();
  const safeLabel = label ?? t('common.loading');

  return (
    // biome-ignore lint/a11y/useSemanticElements: live region pattern em <div>.
    <div
      role="status"
      aria-live="polite"
      data-testid="splash-loader"
      className={cn(
        'flex flex-col items-center justify-center gap-4 text-fg-DEFAULT',
        fullscreen ? 'min-h-screen w-full bg-bg-base' : 'min-h-[40vh] w-full',
        className,
      )}
    >
      <LumeMark size={96} animated title="Lume" />
      <p className="text-xl font-bold lowercase tracking-tight text-brand-900">Lume</p>
      <p className="text-sm font-medium text-fg-muted">{safeLabel}</p>
      {subtitle ? <p className="text-xs text-fg-subtle">{subtitle}</p> : null}
    </div>
  );
}
